import CloseIcon from '@mui/icons-material/Close';
import { Box, Drawer, Grid, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUserQuery } from '@/api/queries/useAuth';
import { useCommentsDrawer } from '@/comments';
import { CommentBlock } from '@/components/CommentBlock/CommentBlock';
import { CommentInputWithButton } from '@/components/CommentBlock/CommentInputWithButton';
import { CommentItemHeader } from '@/components/CommentBlock/CommentItemHeader';
import type { IComment } from '@/types';
import { getUserName } from '@/utils/helpers';

interface CommentInputProps {
    commentableModel: string;
    commentableId: number;
    position: string;
    textAtomContent: string;
}

const CommentInput = (props: CommentInputProps) => {
    const [, drawerActions] = useCommentsDrawer();
    const { data: user } = useUserQuery();

    const updatedAt = new Date();
    updatedAt.setHours(updatedAt.getHours() + updatedAt.getTimezoneOffset() / 60);

    const handleCancel = () => drawerActions.hideTopInput();

    if (!user) {
        return null;
    }

    return (
        <Box
            mt="20px"
            sx={{
                borderRadius: '16px',
                border: theme => `1px solid ${theme.custom.c39}`,
                background: theme => theme.palette.background.paper,
                padding: '20px 16px',
            }}
        >
            <CommentItemHeader user={{ ...user, name: getUserName(user) }} updatedAt={updatedAt} />
            <CommentInputWithButton
                initialText={''}
                textAtomContent={props.textAtomContent}
                queryArgs={{
                    commentableModel: props.commentableModel,
                    commentableId: props.commentableId,
                    position: props.position,
                }}
                handleCancel={handleCancel}
            />
        </Box>
    );
};

interface Props extends CommentInputProps {
    showTopInput: boolean;
    filterName?: string;
    open: boolean;
    comments: IComment[];
    openId: number | null;
    onClose: () => void;
    selectionIndex: number;
    drawerPosition?: 'static';
}

export const CommentsMenu = (props: Props) => {
    const { open, comments, onClose, openId, filterName, selectionIndex, drawerPosition = 'fixed', showTopInput } = props;
    const { t } = useTranslation();
    const theme = useTheme();

    return (
        <Drawer
            open={open}
            variant="persistent"
            anchor="right"
            onClose={onClose}
            PaperProps={{
                sx: [
                    {
                        maxWidth: '360px',
                        position: drawerPosition,
                        right: '0',
                        top: '80px',
                        borderLeft: 'none',
                        paddingRight: '15px',
                        background: theme => theme.palette.background.default,
                    },
                    { [theme.breakpoints.down('lg')]: { width: '300px' } },
                ],
            }}
            sx={{ height: '100%' }}
        >
            <Grid
                container
                alignItems="center"
                justifyContent="space-between"
                sx={{
                    borderBottom: theme => `1px solid ${theme.custom.c5}`,
                    marginBottom: '20px',
                    height: '36px',
                }}
            >
                <Typography variant="subtitle1" pb="8px" display="inline-block" color={theme.custom.c1} fontWeight="600">
                    {filterName ? t('comment.with-filter', { filterName }) : t('comment.comment')}
                </Typography>
                <Box
                    sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                    onClick={onClose}
                    children={<CloseIcon fontSize="small" />}
                />
            </Grid>
            {showTopInput && selectionIndex === -1 && <CommentInput {...props} />}
            <div className="flex flex-wrap w-full gap-[12px] mt-[12px]">
                {comments.map((item, index) => (
                    <>
                        <CommentBlock {...item} key={item.id} open={item.id === openId} scrollToId={openId} />
                        {showTopInput && index === selectionIndex && <CommentInput {...props} />}
                    </>
                ))}
            </div>
            {showTopInput && comments.length + 1 === selectionIndex && <CommentInput {...props} />}
        </Drawer>
    );
};
